import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  alpha,
  useTheme,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  AccessTime,
  Person,
  LocationOn,
  Refresh,
  Schedule,
  EventNote,
  Timer,
  CheckCircle,
  Pending,
  Work,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import moment from "moment";
import { getVisitPlan } from "../../../api/services/visitplan";
import DataTable from "../../../components/Table/DataTable";
import { QK } from "../../../base/qk";
import ScheduleModal from "../../VisitPlanScreen/VisitPlanModal/ScheduleModal";
import UpdateVisitModal from "../../../components/Clients/ClientDetails/WhatElse/VisitAnalysis/UpdateVisitModal";
import EditScheduleModal from "../../VisitPlanScreen/VisitPlanModal/EditScheduleModal";

const WeeklyAssignment = ({ candidateId }) => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showScheduleUpdateModal, setShowScheduleUpdateModal] = useState(false);
  const [visitId, setVisitId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [area, setArea] = useState(null);

  const {
    data: visitPlanData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    [QK.visitPlan, candidateId],
    () => getVisitPlan(1, "", candidateId, "", "", "", "", ""),
    {
      enabled: !!candidateId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  useEffect(() => {
    if (candidateId) {
      refetch();
    }
  }, [candidateId, refetch]);

  const formatTime = (time) => {
    if (!time) return "N/A";
    return moment(time, "HH:mm:ss").format("h:mm A");
  };

  const getStatusColor = (isAssigned) => {
    return isAssigned ? theme.palette.success.main : theme.palette.warning.main;
  };

  const getStatusLabel = (isAssigned) => {
    return isAssigned ? "Assigned" : "Unassigned";
  };

  const getStatusIcon = (isAssigned) => {
    return isAssigned ? <CheckCircle sx={{ fontSize: 14 }} /> : <Pending sx={{ fontSize: 14 }} />;
  };

  const getShiftColor = (shiftColor) => {
    return shiftColor || theme.palette.primary.main;
  };

  const handleOpenModal = (row) => {
    setVisitId(row.id);
    setShowModal(true);
  };

  const handleUpdateOpenModal = (row) => {
    setVisitId(row.id);
    setShowUpdateModal(true);
    setClientId(row.client);
  };

  const handleScheduleUpdateOpenModal = (row) => {
    setVisitId(row.id);
    setClientId(row.client);
    setArea(row.area_id);
    setShowScheduleUpdateModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setVisitId(null);
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setVisitId(null);
    setClientId(null);
  };

  const handleCloseScheduleUpdateModal = () => {
    setShowScheduleUpdateModal(false);
    setVisitId(null);
    setClientId(null);
    setArea(null);
  };

  const headers = [
    { key: "visit_name", label: "Visit Name" },
    { key: "client_name", label: "Client" },
    { key: "location", label: "Location" },
    { key: "area", label: "Area" },
    { key: "time_required_from", label: "Time From" },
    { key: "time_required_to", label: "Time To" },
    { key: "visit_type_display", label: "Visit Type" },
    { key: "shift", label: "Shift" },
    { key: "status", label: "Status" },
  ];

  const renderCell = (item, header) => {
    const key = typeof header === 'string' ? header : header.key;

    switch (key) {
      case "visit_name":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                display: "inline-block",
                padding: "4px 10px",
                borderRadius: "6px",
                backgroundColor: item.shift_details?.shift_color || "#ddd",
                fontWeight: 600,
                fontSize: "0.78rem",
                color: "#1A1A2E",
              }}
            >
              {item.visit_name || "-"}
            </Box>
            {item?.is_career_assigned ? (
              <Tooltip title="Career Assigned">
                <CheckCircle sx={{ fontSize: 16, color: theme.palette.success.main }} />
              </Tooltip>
            ) : (
              <Tooltip title="Career Not Assigned">
                <Pending sx={{ fontSize: 16, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
          </Box>
        );

      case "client_name":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Person sx={{ fontSize: 14, color: "#94A3B8" }} />
            <Typography variant="body2">{item.client_name || "-"}</Typography>
          </Box>
        );

      case "location":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 14, color: "#94A3B8" }} />
            <Typography variant="body2">{item.location || "-"}</Typography>
          </Box>
        );

      case "area":
        return (
          <Chip
            label={item.area || "-"}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.65rem",
              fontWeight: 500,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              color: theme.palette.info.main,
              borderRadius: "4px",
            }}
          />
        );

      case "time_required_from":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 14, color: "#94A3B8" }} />
            <Typography variant="body2">{formatTime(item.time_required_from)}</Typography>
          </Box>
        );

      case "time_required_to":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 14, color: "#94A3B8" }} />
            <Typography variant="body2">{formatTime(item.time_required_to)}</Typography>
          </Box>
        );

      case "visit_type_display":
        return (
          <Chip
            label={item.visit_type_display || "-"}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.65rem",
              fontWeight: 500,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              borderRadius: "4px",
            }}
          />
        );

      case "shift":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: getShiftColor(item.shift_details?.shift_color),
              }}
            />
            <Typography variant="body2">
              {item.shift_details?.shift_name || "-"}
            </Typography>
          </Box>
        );

      case "status":
        return (
          <Chip
            icon={getStatusIcon(item.is_career_assigned)}
            label={getStatusLabel(item.is_career_assigned)}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: alpha(getStatusColor(item.is_career_assigned), 0.08),
              color: getStatusColor(item.is_career_assigned),
              borderRadius: "6px",
              "& .MuiChip-icon": {
                fontSize: 14,
              },
            }}
          />
        );

      default:
        return item[key] || "-";
    }
  };

  const customActions = (item) => (
    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
      <Tooltip title="Schedule Visit" arrow>
        <IconButton
          size="small"
          onClick={() => handleOpenModal(item)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#2469F0",
              bgcolor: alpha("#2469F0", 0.06),
            },
          }}
        >
          <Schedule sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {item.visit_type === 1 ? (
        <Tooltip title="Edit Visit" arrow>
          <IconButton
            size="small"
            onClick={() => handleUpdateOpenModal(item)}
            sx={{
              color: "#667085",
              p: 0.5,
              "&:hover": {
                color: "#F59E0B",
                bgcolor: alpha("#F59E0B", 0.06),
              },
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Edit Schedule" arrow>
          <IconButton
            size="small"
            onClick={() => handleScheduleUpdateOpenModal(item)}
            sx={{
              color: "#667085",
              p: 0.5,
              "&:hover": {
                color: "#F59E0B",
                bgcolor: alpha("#F59E0B", 0.06),
              },
            }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="View Timeline" arrow>
        <IconButton
          size="small"
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#8B5CF6",
              bgcolor: alpha("#8B5CF6", 0.06),
            },
          }}
        >
          <Timer sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ borderRadius: "12px" }}>
        Failed to load weekly assignments: {error?.message || "Please try again later"}
      </Alert>
    );
  }

  const visits = visitPlanData?.results || [];

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="#1A1A2E">
            Weekly Assignment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {visits.length} visits found
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Paper
          sx={{
            px: 2.5,
            py: 1.5,
            borderRadius: "12px",
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flex: "0 0 auto",
          }}
        >
          <EventNote sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Candidate ID
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {candidateId || "N/A"}
            </Typography>
          </Box>
        </Paper>

        <Paper
          sx={{
            px: 2.5,
            py: 1.5,
            borderRadius: "12px",
            bgcolor: alpha(theme.palette.info.main, 0.04),
            border: `1px solid ${alpha(theme.palette.info.main, 0.08)}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flex: "0 0 auto",
          }}
        >
          <Work sx={{ color: theme.palette.info.main, fontSize: 20 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Total Visits
            </Typography>
            <Typography variant="body2" fontWeight={600} color={theme.palette.info.main}>
              {visits.length}
            </Typography>
          </Box>
        </Paper>
      </Stack>

      {visits.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: "12px",
            bgcolor: alpha(theme.palette.grey[500], 0.04),
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No assignments found for this candidate
          </Typography>
        </Paper>
      ) : (
        <DataTable
          data={visits}
          headers={headers}
          renderCell={renderCell}
          customActions={customActions}
          actionColumnLabel="Actions"
        />
      )}

      {/* Modals */}
      <ScheduleModal
        visitId={visitId}
        show={showModal}
        handleClose={handleCloseModal}
      />

      <UpdateVisitModal
        visitId={visitId}
        show={showUpdateModal}
        clientIdProp={clientId}
        handleClose={handleCloseUpdateModal}
      />

      <EditScheduleModal
        visitId={visitId}
        clientId={clientId}
        show={showScheduleUpdateModal}
        area={area}
        handleClose={handleCloseScheduleUpdateModal}
      />
    </Box>
  );
};

export default WeeklyAssignment;